import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BulkVendorUpload } from "@/components/BulkVendorUpload";
import { AdminSponsorships } from "@/components/AdminSponsorships";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wallet, Banknote, Ticket, RefreshCw, Send, AlertTriangle, Download, Store } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const cls = s.includes("success") || s.includes("completed") ? "bg-emerald-100 text-emerald-800"
    : s.includes("fail") || s.includes("reversed") || s.includes("expired") || s.includes("cancelled") ? "bg-red-100 text-red-700"
    : "bg-amber-100 text-amber-800";
  return <Badge className={cls} data-testid={`status-${s}`}>{status || "—"}</Badge>;
}

function AddVendorForm({ onSaved }) {
  const blank = { owner: "", business_name: "", phone: "", alt_phone: "", address: "", lga: "", state: "", landmark: "", moniepoint_account_number: "", account_name: "" };
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const fields = [["owner", "Vendor / contact name"], ["business_name", "Shop name (optional)"], ["phone", "Phone"], ["alt_phone", "Additional phone (optional)"], ["address", "Shop address"], ["landmark", "Nearest landmark (optional)"], ["lga", "LGA"], ["state", "State"], ["moniepoint_account_number", "Moniepoint account number"], ["account_name", "Account name"]];
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/vendors", f);
      if (data.created) toast.success(`Vendor ${data.vendor_id} created (pending). Monnify name: ${data.monnify_account_name || "not found"}`);
      else toast.info(data.message);
      setF(null); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  if (!f) return <Button size="sm" variant="outline" data-testid="add-vendor-btn" onClick={() => setF(blank)} className="mt-3">+ Add vendor</Button>;
  return (
    <form onSubmit={save} className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5 rounded-xl border border-emerald-100 p-3" data-testid="add-vendor-form">
      {fields.map(([k, label]) => (
        <Input key={k} data-testid={`add-vendor-${k}`} placeholder={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
          required={!["business_name", "alt_phone", "landmark"].includes(k)} className="h-9 text-sm" />
      ))}
      <div className="flex gap-2 lg:col-span-5">
        <Button type="submit" size="sm" disabled={busy} data-testid="add-vendor-submit" className="bg-emerald-600 hover:bg-emerald-700">{busy ? "Saving…" : "Create vendor (pending)"}</Button>
        <Button type="button" size="sm" variant="ghost" data-testid="add-vendor-cancel" onClick={() => setF(null)}>Cancel</Button>
      </div>
    </form>
  );
}

function SponsorPayments() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const load = useCallback(() => { api.get("/admin/payments").then(({ data }) => { setRows(data.payments); setTotals(data.totals); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const recheck = async (ref) => {
    try { const { data } = await api.post(`/admin/payments/${ref}/paystack-verify`); toast.success(`${ref}: ${data.result} (Paystack: ${data.paystack_status})`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="sponsor-payments-panel">
      <h2 className="font-display font-bold text-lg text-emerald-950">Sponsor Payments (Paystack / transfer)</h2>
      {totals && (
        <div className="mt-3 grid gap-3 grid-cols-1 sm:grid-cols-3" data-testid="sponsor-payments-totals">
          {[["Programme value received", totals.programme_value, "value"], ["Delivery & Impact Fee (10%)", totals.delivery_fee, "fee"], ["Total received", totals.total, "total"]].map(([k, v, id]) => (
            <div key={k} className="rounded-xl border border-emerald-100 p-3" data-testid={`payments-total-${id}`}><div className="text-xs text-gray-500">{k}</div><div className="mt-1 font-bold text-lg text-emerald-950 tabular-nums">{naira(v)}</div></div>
          ))}
        </div>
      )}
      <p className="mt-2 text-xs text-gray-400">Totals count confirmed (paid) payments only.</p>
      <div className="mt-3 overflow-auto max-h-80">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Reference</th><th>Sponsor</th><th>Programme Value</th><th>Fee (10%)</th><th>Total</th><th>Method</th><th>Status</th><th>Receipt</th><th>Action</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-4 text-gray-500">No sponsor payments yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.ref} className="border-t border-gray-100" data-testid={`payment-row-${p.ref}`}>
                <td className="py-2 font-mono text-xs">{p.ref}</td>
                <td className="text-xs">{p.sponsor_name}</td>
                <td className="text-xs">{naira(p.programme_value ?? p.total)}</td>
                <td className="text-xs" data-testid={`payment-fee-${p.ref}`}>{naira(p.delivery_fee || 0)}</td>
                <td className="text-xs">{naira(p.total)}</td>
                <td className="text-xs">{p.method}</td>
                <td><Badge className={p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "amount_mismatch" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}>{p.status}</Badge></td>
                <td className="text-xs">{p.receipt_sent ? "Emailed" : "—"}</td>
                <td>{p.method === "paystack" && p.status !== "paid" && <Button size="sm" variant="outline" className="h-8" data-testid={`paystack-recheck-${p.ref}`} onClick={() => recheck(p.ref)}>Re-check Paystack</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UssdJourneySwitch() {
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/ussd/journey").then(({ data }) => setJ(data)).catch(() => {}); }, []);
  const flip = async () => {
    const value = j.value === "v2" ? "v1" : "v2";
    setBusy(true);
    try { await api.put("/admin/ussd/journey", { value }); setJ({ ...j, value }); toast.success(`USSD journey switched to ${value === "v2" ? "NEW" : "OLD (rollback)"}`); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!j) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-white p-4 text-sm" data-testid="ussd-journey-card">
      <div>
        <b>USSD journey (*7006*99#):</b> <span data-testid="ussd-journey-value">{j.value === "v2" ? "NEW — Beneficiary / Food Vendor / Find a Vendor" : "OLD — rollback menu"}</span>
        {!j.gateway_secured && <div className="text-xs text-amber-700" data-testid="ussd-gateway-warning">USSD voucher redemption is blocked until the USSD gateway is secured (USSD_TMF_TOKEN or USSD_GATEWAY_IPS).</div>}
      </div>
      <Button size="sm" variant="outline" disabled={busy} onClick={flip} data-testid="ussd-journey-toggle">{j.value === "v2" ? "Roll back to old menu" : "Switch to new menu"}</Button>
    </div>
  );
}

export default function AdminPayouts() {
  const [wallet, setWallet] = useState(null);
  const [cashouts, setCashouts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [paycodes, setPaycodes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [valEvents, setValEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [monitor, setMonitor] = useState([]);
  const [mq, setMq] = useState("");
  const [mstate, setMstate] = useState("");

  const loadFin = useCallback(async () => {
    try {
      const params = {};
      if (mq) params.q = mq;
      if (mstate) params.state = mstate;
      const [s, t] = await Promise.all([
        api.get("/admin/financials/summary"),
        api.get("/admin/financials/transactions", { params }),
      ]);
      setSummary(s.data); setMonitor(t.data.transactions);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [mq, mstate]);

  const load = useCallback(async () => {
    try {
      const [w, c, b, p, vd, rd, ve] = await Promise.all([
        api.get("/admin/monnify/wallet"),
        api.get("/admin/cashouts"),
        api.get("/admin/cashouts/batches"),
        api.get("/admin/paycodes"),
        api.get("/admin/vendors"),
        api.get("/admin/redemptions"),
        api.get("/admin/monnify/validation-events"),
      ]);
      setWallet(w.data); setCashouts(c.data.cashouts); setBatches(b.data.batches); setPaycodes(p.data.paycodes); setVendors(vd.data.vendors); setRedemptions(rd.data.redemptions); setValEvents(ve.data.events);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFin(); }, [loadFin]);

  const processBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cashouts/batch");
      toast.success(`Batch ${data.batch.batch_reference}: ${data.processed} transfer(s) submitted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const syncBatch = async (ref) => {
    try { const { data } = await api.post(`/admin/cashouts/batch/${ref}/sync`); toast.success(`Status: ${data.batch_status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const syncPaycode = async (ref) => {
    try { const { data } = await api.post(`/admin/paycodes/${ref}/sync`); toast.success(`Status: ${data.status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const approveVendor = async (id) => {
    try { const { data } = await api.post(`/admin/vendors/${id}/approve`); toast.success(`${id} verified → ${data.account_name || "account"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deactivateVendor = async (id) => {
    try { await api.post(`/admin/vendors/${id}/deactivate`); toast.success(`${id} deactivated`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const syncRedemption = async (ref) => {
    try { const { data } = await api.post(`/admin/redemptions/${ref}/sync`); toast.success(data.message || `Status: ${data.transaction_status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const releaseRedemption = async (ref) => {
    try { const { data } = await api.post(`/admin/redemptions/${ref}/release`); toast.success(data.message); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/financials/reconciliation.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "tmf-reconciliation.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!loaded) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  if (!wallet) return (
    <div><Navbar /><div className="max-w-6xl mx-auto px-4 py-16 text-center text-gray-500">Couldn't load payout data. Please refresh.</div></div>
  );
  const pendingCount = cashouts.filter((c) => c.status === "pending").length;
  const bal = wallet.balance;

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Monnify Wallet · Payouts</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">Cash-out & Paycode Operations</h1>
        <UssdJourneySwitch />
        <SponsorPayments />
        <AdminSponsorships />

        {!wallet.disburse_enabled && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="disburse-disabled-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div><b>Live disbursement is OFF (safe mode).</b> Paycodes & cash-outs are simulated and no money leaves the wallet. After Monnify enables disbursements, whitelists this server IP and disables MFA, set <code>MONNIFY_DISBURSE_ENABLED=true</code> to go live.</div>
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-emerald-100 bg-white p-5" data-testid="wallet-balance-card">
            <div className="flex items-center justify-between"><span className="text-xs text-gray-500 font-medium">Wallet Available Balance</span><Wallet className="w-4 h-4 text-emerald-500" /></div>
            <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{bal ? naira(bal.available_balance) : (wallet.error ? "—" : "…")}</div>
            <div className="text-xs text-gray-400 mt-1">Acct {wallet.wallet_account} {wallet.error && <span className="text-red-500">· {wallet.error}</span>}</div>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-white p-5">
            <div className="flex items-center justify-between"><span className="text-xs text-gray-500 font-medium">Pending Cash-outs</span><Banknote className="w-4 h-4 text-amber-500" /></div>
            <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{pendingCount}</div>
            <div className="text-xs text-gray-400 mt-1">{naira(cashouts.filter((c) => c.status === "pending").reduce((a, c) => a + c.amount, 0))} total</div>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-white p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between"><span className="text-xs text-gray-500 font-medium">Batch Disbursement</span><Send className="w-4 h-4 text-emerald-500" /></div>
            <Button data-testid="process-batch-btn" onClick={processBatch} disabled={busy || pendingCount === 0} className="mt-3 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Process Batch (up to 100)`}
            </Button>
          </div>
        </div>

        {/* Vendor settlement redemptions */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="redemptions-panel">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-600" /> Voucher Redemptions (Vendor Settlement)</h2>
          <p className="mt-1 text-xs text-gray-500">Each redemption pays a vendor directly. Use "Check status" to re-query Monnify by reference and resolve a PENDING/UNCONFIRMED payment — a voucher is only marked REDEEMED after a confirmed Monnify disbursement.</p>
          <div className="mt-4 overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Reference</th><th>Voucher</th><th>Vendor</th><th>Amount</th><th>Monnify Ref</th><th>Status</th><th>Action</th></tr></thead>
              <tbody data-testid="redemptions-table">
                {redemptions.length === 0 && <tr><td colSpan={7} className="py-4 text-gray-500">No redemptions yet.</td></tr>}
                {redemptions.map((r) => {
                  const st = r.transaction_status;
                  const done = st === "REDEEMED";
                  const failed = st === "PAYMENT_FAILED";
                  const cls = done ? "bg-emerald-100 text-emerald-800" : failed ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800";
                  return (
                    <tr key={r.internal_reference} className="border-t border-gray-100" data-testid={`redemption-row-${r.internal_reference}`}>
                      <td className="py-2 font-mono text-xs">{r.internal_reference}</td>
                      <td className="font-mono text-xs">{r.tmf_voucher_id || r.voucher_code}</td>
                      <td className="text-xs">{r.vendor_name}<div className="text-gray-400">{r.vendor_id}</div></td>
                      <td className="font-semibold text-emerald-700">{naira(r.amount)}</td>
                      <td className="font-mono text-[11px] text-gray-500">{r.provider_reference || "—"}</td>
                      <td><Badge className={cls} data-testid={`redemption-status-${r.internal_reference}`}>{st}{r.simulated ? " · SIM" : ""}</Badge></td>
                      <td>{failed && !r.provider_reference && r.voucher_status === "redemption_pending" && <Button size="sm" variant="outline" data-testid={`release-redemption-${r.internal_reference}`} onClick={() => releaseRedemption(r.internal_reference)} className="h-8 border-red-200 text-red-700">Free up voucher</Button>}
                        {failed && r.voucher_status && r.voucher_status !== "redemption_pending" && <span className="text-[11px] text-gray-400" data-testid={`redemption-voucher-${r.internal_reference}`}>Voucher {r.voucher_status}</span>}
                        {!done && !failed && <Button size="sm" variant="outline" data-testid={`sync-redemption-${r.internal_reference}`} onClick={() => syncRedemption(r.internal_reference)} className="h-8"><RefreshCw className="w-3.5 h-3.5 mr-1" />Check status</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="validation-events-panel">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-emerald-600" /> Monnify Transaction Validation Log</h2>
          <p className="mt-1 text-xs text-gray-500">Every payout-authorisation callback from Monnify and TMF's decision. HOLD = not approved without an explicit reject (Monnify retries, then cancels; no PND).</p>
          <div className="mt-4 overflow-auto max-h-80">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Time</th><th>Reference</th><th>Type</th><th>Amount</th><th>Decision</th><th>Reason</th></tr></thead>
              <tbody data-testid="validation-events-table">
                {valEvents.length === 0 && <tr><td colSpan={6} className="py-4 text-gray-500">No validation callbacks yet.</td></tr>}
                {valEvents.map((ev) => (
                  <tr key={ev.id} className="border-t border-gray-100" data-testid={`validation-event-${ev.id}`}>
                    <td className="py-2 text-xs text-gray-500">{new Date(ev.received_at).toLocaleString()}</td>
                    <td className="font-mono text-xs">{ev.reference || "—"}</td>
                    <td className="text-xs">{ev.type}</td>
                    <td className="text-xs">{ev.amount != null ? naira(ev.amount) : "—"}</td>
                    <td><Badge className={ev.decision === "APPROVE" ? "bg-emerald-100 text-emerald-800" : ev.decision === "REJECT" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}>{ev.decision}</Badge></td>
                    <td className="text-xs text-gray-600">{ev.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Vendor payment-account verification */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="vendor-verification">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Store className="w-5 h-5 text-emerald-600" /> Vendor Payment Verification</h2>
          <p className="mt-1 text-xs text-gray-500">Approve a vendor's Moniepoint settlement account before they can redeem vouchers. Approving runs a Monnify name-enquiry to confirm the account name.</p>
          <AddVendorForm onSaved={load} />
          <BulkVendorUpload onSaved={load} />
          <div className="mt-4 overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Vendor</th><th>Account</th><th>Account Name</th><th>Status</th><th>Action</th></tr></thead>
              <tbody data-testid="vendors-table">
                {vendors.length === 0 && <tr><td colSpan={5} className="py-4 text-gray-500">No vendors yet.</td></tr>}
                {vendors.map((vn) => {
                  const acct = vn.moniepoint_account_number || vn.pending_account_number || vn.bank_account || "—";
                  const verified = vn.account_verified && vn.status === "verified";
                  return (
                    <tr key={vn.vendor_id} className="border-t border-gray-100" data-testid={`vendor-row-${vn.vendor_id}`}>
                      <td className="py-2"><div className="font-medium">{vn.business_name}</div><div className="font-mono text-xs text-gray-400">{vn.vendor_id} · {vn.state}</div></td>
                      <td className="font-mono text-xs">{acct}</td>
                      <td className="text-xs">{vn.account_name || "—"}{vn.account_name_entered && vn.account_name && vn.account_name_match === false && <div className="text-red-600" data-testid={`vendor-name-mismatch-${vn.vendor_id}`}>Supplied: {vn.account_name_entered} — check before approving</div>}{vn.pending_account_name && <div className="text-amber-700" data-testid={`vendor-pending-name-${vn.vendor_id}`}>Requested: {vn.pending_account_name}{vn.pending_account_number ? ` · ${vn.pending_account_number}` : ""}</div>}</td>
                      <td><Badge className={verified ? "bg-emerald-100 text-emerald-800" : vn.status === "deactivated" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}>{verified ? "VERIFIED" : (vn.status || "pending").toUpperCase()}</Badge></td>
                      <td>
                        <div className="flex gap-2">
                          {!verified && <Button size="sm" data-testid={`approve-vendor-${vn.vendor_id}`} onClick={() => approveVendor(vn.vendor_id)} className="bg-emerald-600 hover:bg-emerald-700 h-8">Approve</Button>}
                          {vn.status !== "deactivated" && <Button size="sm" variant="outline" data-testid={`deactivate-vendor-${vn.vendor_id}`} onClick={() => deactivateVendor(vn.vendor_id)} className="h-8 text-red-600 border-red-200">Deactivate</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {summary?.low_balance && (
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="low-balance-banner">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div><b>Monnify wallet low balance.</b> Available {summary.wallet ? naira(summary.wallet.available_balance) : "—"} is below the {naira(summary.low_balance_threshold)} threshold. Fund the wallet before further disbursements.</div>
          </div>
        )}

        {summary && (
          <div className="mt-8" data-testid="fin-summary">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display font-bold text-lg text-emerald-950">Financial Summary</h2>
              <Button data-testid="export-reconciliation-btn" size="sm" variant="outline" onClick={exportCsv} className="border-emerald-300 text-emerald-800">
                <Download className="w-4 h-4 mr-2" /> Export Reconciliation (CSV)
              </Button>
            </div>
            <div className="mt-3 grid gap-3 grid-cols-2 md:grid-cols-4">
              {[["Allocated", naira(summary.allocated)], ["Food Disbursed", naira(summary.food_disbursed)], ["Cash Disbursed", naira(summary.cash_disbursed)], ["Outstanding", naira(summary.outstanding_balance)], ["Successful", summary.successful], ["Pending", summary.pending], ["Failed", summary.failed], ["Unreconciled", summary.unreconciled], ...(summary.funding ? [["Sponsor Funding Received", naira(summary.funding.total)], ["Delivery & Impact Fee (10%)", naira(summary.funding.delivery_fee)]] : [])].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-emerald-100 bg-white p-4"><div className="text-xs text-gray-500">{k}</div><div className="mt-1 font-bold text-xl text-emerald-950 tabular-nums">{v}</div></div>
              ))}
            </div>
          </div>
        )}

        {/* Monnify Transaction Monitor */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="txn-monitor">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display font-bold text-lg text-emerald-950">Monnify Transaction Monitor</h2>
            <div className="flex items-center gap-2">
              <input data-testid="monitor-search" value={mq} onChange={(e) => setMq(e.target.value)} placeholder="TMF ID, voucher, ref, phone…" className="h-9 rounded-lg border border-gray-200 px-3 text-sm" />
              <select data-testid="monitor-state" value={mstate} onChange={(e) => setMstate(e.target.value)} className="h-9 rounded-lg border border-gray-200 px-2 text-sm">
                <option value="">All states</option>
                {["PAYCODE_ISSUED", "REQUESTED", "SUBMITTED_TO_MONNIFY", "PROCESSING", "SUCCESSFUL", "FAILED", "REVERSED"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button size="sm" data-testid="monitor-search-btn" onClick={loadFin} className="bg-emerald-600 hover:bg-emerald-700">Search</Button>
            </div>
          </div>
          <div className="mt-4 overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">TMF Ref</th><th>Type</th><th>Beneficiary</th><th>Amount</th><th>State</th><th>Monnify Ref</th></tr></thead>
              <tbody data-testid="monitor-table">
                {monitor.length === 0 && <tr><td colSpan={6} className="py-4 text-gray-500">No Monnify transactions.</td></tr>}
                {monitor.map((t) => (
                  <tr key={t.txn_id} className="border-t border-gray-100">
                    <td className="py-2 font-mono text-xs">{t.txn_id}</td>
                    <td className="capitalize">{t.channel}</td>
                    <td className="text-xs">{t.tmf_id}<div className="text-gray-400">{t.beneficiary_phone}</div></td>
                    <td className="font-semibold text-emerald-700">{naira(t.amount)}</td>
                    <td><StatusBadge status={t.state} /></td>
                    <td className="font-mono text-xs">{t.monnify_reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pending cash-outs */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-600" /> Cash-out Requests</h2>
          <div className="mt-4 overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Reference</th><th>Beneficiary</th><th>Account</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody data-testid="cashouts-table">
                {cashouts.length === 0 && <tr><td colSpan={5} className="py-4 text-gray-500">No cash-out requests yet.</td></tr>}
                {cashouts.map((c) => (
                  <tr key={c.reference} className="border-t border-gray-100">
                    <td className="py-2 font-mono text-xs">{c.reference}</td>
                    <td>{c.tmf_id}</td>
                    <td className="text-xs">{c.account_name || "—"}<div className="text-gray-400">{c.account_number} · {c.bank_code}</div></td>
                    <td className="font-semibold text-emerald-700">{naira(c.amount)}</td>
                    <td><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Batches */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Send className="w-5 h-5 text-emerald-600" /> Disbursement Batches</h2>
          <div className="mt-4 space-y-2" data-testid="batches-list">
            {batches.length === 0 && <p className="text-sm text-gray-500">No batches yet.</p>}
            {batches.map((b) => (
              <div key={b.batch_reference} className="flex items-center justify-between border-b border-gray-100 pb-2">
                <div><div className="font-mono text-xs">{b.batch_reference}</div><div className="text-xs text-gray-400">{b.count} transfers · {naira(b.total_amount)}{b.simulated ? " · SIMULATED" : ""}</div></div>
                <div className="flex items-center gap-2"><StatusBadge status={b.monnify_status || b.status} />
                  {!b.simulated && <Button size="sm" variant="outline" onClick={() => syncBatch(b.batch_reference)}><RefreshCw className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Paycodes */}
        <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6">
          <h2 className="font-display font-bold text-lg text-emerald-950 flex items-center gap-2"><Ticket className="w-5 h-5 text-emerald-600" /> Food Paycodes</h2>
          <div className="mt-4 space-y-2" data-testid="paycodes-list">
            {paycodes.length === 0 && <p className="text-sm text-gray-500">No paycodes yet.</p>}
            {paycodes.map((p) => (
              <div key={p.paycode_reference} className="flex items-center justify-between border-b border-gray-100 pb-2">
                <div><div className="font-mono text-sm text-emerald-800">{p.paycode || "—"}</div><div className="text-xs text-gray-400">{p.tmf_id} · {naira(p.amount)}{p.simulated ? " · SIMULATED" : ""}</div></div>
                <div className="flex items-center gap-2"><StatusBadge status={p.status} />
                  {!p.simulated && <Button size="sm" variant="outline" onClick={() => syncPaycode(p.paycode_reference)}><RefreshCw className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
